import {
  EmployeeBreach,
  PaginatedResponseEmployeeBreach,
} from '@electricjs/core_entity-client';

import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import { EmployeeBreachesResults } from '@/types/employeeBreach';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  MutationActionCreatorResult,
  MutationDefinition,
  RetryOptions,
} from '@reduxjs/toolkit/query';

export type ResolveEmployeeBreachArgs = {
  organizationId: string;
  employeeId: string;
  breachId: string;
};

export type MarkEmployeeBreachResolvedResponse =
  | Promise<{ data?: void }>
  | MutationActionCreatorResult<
      MutationDefinition<
        ResolveEmployeeBreachArgs,
        BaseQueryFn<
          string | FetchArgs,
          unknown,
          FetchBaseQueryError,
          object & RetryOptions,
          FetchBaseQueryMeta
        >,
        string,
        void,
        'apiSlice'
      >
    >;

const mapEmployeeBreach = (result: EmployeeBreach) => ({
  employee: {
    id: result.employee.id,
    firstName: result.employee.first_name,
    lastName: result.employee.last_name,
    email: result.employee.email,
  },
  breach: {
    id: result.breach.id,
    name: result.breach.name,
    displayName: result.breach.display_name,
    domainUrl: result.breach.domain_url,
    dataClasses: result.breach.data_classes,
    logoUrl: result.breach.logo_url,
    breachedAt: result.breach.breached_at,
  },
  resolvedAt: result.resolved_at,
  resolvedBy: result.resolved_by,
  createdAt: result.created_at,
  updatedAt: result.updated_at,
});

export const employeeBreachApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    markEmployeeBreachResolved: builder.mutation<
      void,
      ResolveEmployeeBreachArgs
    >({
      query: ({ organizationId, employeeId, breachId }) => {
        return {
          url: `/v1/organizations/${organizationId}/employees/${employeeId}/breaches/${breachId}/resolve`,
          method: 'POST',
        };
      },
      invalidatesTags: [TagTypes.EmployeeBreach],
    }),
    listEmployeeBreachesByOrganizationId: builder.query<
      EmployeeBreachesResults,
      { organizationId: string }
    >({
      providesTags: [TagTypes.EmployeeBreach],
      query: ({ organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/breaches`,
        };
      },
      transformResponse: (response: PaginatedResponseEmployeeBreach) => {
        return response.results.map(mapEmployeeBreach);
      },
    }),
    listEmployeeBreachesByEmployeeId: builder.query<
      EmployeeBreachesResults,
      { organizationId: string; employeeId: string }
    >({
      providesTags: [TagTypes.EmployeeBreach],
      query: ({ organizationId, employeeId }) => {
        return {
          url: `/v1/organizations/${organizationId}/employees/${employeeId}/breaches`,
        };
      },
      transformResponse: (response: PaginatedResponseEmployeeBreach) => {
        return response.results.map(mapEmployeeBreach);
      },
    }),
  }),
});

// export auto-generated hooks
export const {
  useListEmployeeBreachesByOrganizationIdQuery,
  useListEmployeeBreachesByEmployeeIdQuery,
  useMarkEmployeeBreachResolvedMutation,
} = employeeBreachApiSlice;
